import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { SalesTeamService } from '../../services/salesTeam.service';
import {
  SalesDepartment, SalesTeam, SalesTeamLink, SalesTeamsOverview,
} from '../../interfaces/sales/salesTeam.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import SalespersonSelect from './scope/SalespersonSelect';
import { helpWhy } from '../../help/why';

const cell = { flex: '1 1 220px', maxWidth: 320 };

/** Picker plus Add button: choose an employee, then add them in one role (head, manager, member). */
const AddPerson: React.FC<{ label: string; busy: boolean; onAdd: (userId: string) => Promise<void> }> = ({ label, busy, onAdd }) => {
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <SalespersonSelect label={label} value={userId} onChange={setUserId} sx={cell} />
      <Button
        size="small" startIcon={<AddIcon />} disabled={!userId || busy}
        {...helpWhy(!userId && !busy ? { kind: 'status', message: 'Choose a person first.' } : null)}
        onClick={async () => { if (userId) { await onAdd(userId); setUserId(null); } }}
      >
        Add
      </Button>
    </Box>
  );
};

const LinkChips: React.FC<{ links: SalesTeamLink[]; busy: boolean; onRemove: (userId: string) => void }> = ({ links, busy, onRemove }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {links.length === 0 && <Typography variant="body2" color="text.secondary">None</Typography>}
    {links.map(l => (
      <Tooltip key={l.linkId} title={`Since ${formatDate(l.effectiveFrom)}`}>
        <Chip label={l.userName} onDelete={busy ? undefined : () => onRemove(l.userId)} />
      </Tooltip>
    ))}
  </Box>
);

const NameDialog: React.FC<{ title: string; initial: string; onClose: () => void; onSave: (name: string) => Promise<void> }> = ({ title, initial, onClose, onSave }) => {
  const [name, setName] = useState(initial);
  const [saving, setSaving] = useState(false);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-page="sales.teams.nameDialog.page">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent data-help-section="sales.teams.nameDialog.section.form" sx={{ pt: 1 }}>
        <TextField data-help="sales.teams.nameDialog.name" autoFocus fullWidth label="Name" value={name} onChange={e => setName(e.target.value)} inputProps={{ maxLength: 120 }} />
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.teams.nameDialog.cancel" onClick={onClose}>Cancel</Button>
        <Button
          data-help="sales.teams.nameDialog.save" variant="contained" disabled={!name.trim() || saving}
          onClick={async () => { setSaving(true); try { await onSave(name.trim()); onClose(); } catch { /* the toast was shown by the caller; keep the dialog open */ } finally { setSaving(false); } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type NameTarget =
  | { kind: 'newDepartment' }
  | { kind: 'renameDepartment'; dept: SalesDepartment }
  | { kind: 'newTeam'; dept: SalesDepartment }
  | { kind: 'renameTeam'; team: SalesTeam };

/**
 * S0 setup screen: sales departments (with heads) and teams (with managers and members). Removing a person closes
 * the link from today, it never deletes history. One active team per rep.
 */
const SalesTeams: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [data, setData] = useState<SalesTeamsOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [nameTarget, setNameTarget] = useState<NameTarget | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await SalesTeamService.overview(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load sales teams');
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  /** Every setup call returns the refreshed overview. */
  const run = async (fn: () => Promise<SalesTeamsOverview>, fail: string) => {
    try {
      setBusy(true);
      setData(await fn());
    } catch (e) {
      toast.showApiError(e, fail);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const safe = (fn: () => Promise<SalesTeamsOverview>, fail: string) => run(fn, fail).catch(() => undefined);

  const saveName = async (name: string) => {
    const t = nameTarget;
    if (!t) return;
    if (t.kind === 'newDepartment') await run(() => SalesTeamService.createDepartment(institutionId, name), 'Failed to create department');
    else if (t.kind === 'renameDepartment') await run(() => SalesTeamService.renameDepartment(institutionId, t.dept.id, name, t.dept.active), 'Failed to rename department');
    else if (t.kind === 'newTeam') await run(() => SalesTeamService.createTeam(institutionId, t.dept.id, name), 'Failed to create team');
    else await run(() => SalesTeamService.renameTeam(institutionId, t.team.id, t.team.departmentId, name, t.team.active), 'Failed to rename team');
  };

  if (!data) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const nameDialogTitle = !nameTarget ? '' : {
    newDepartment: 'New sales department', renameDepartment: 'Rename department', newTeam: 'New team', renameTeam: 'Rename team',
  }[nameTarget.kind];
  const nameDialogInitial = !nameTarget ? '' : nameTarget.kind === 'renameDepartment' ? nameTarget.dept.name
    : nameTarget.kind === 'renameTeam' ? nameTarget.team.name : '';

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.teams.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h5">Sales Teams</Typography>
        <Button data-help="sales.teams.newDepartment" variant="contained" startIcon={<AddIcon />} onClick={() => setNameTarget({ kind: 'newDepartment' })}>New department</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A rep belongs to one team at a time. Managers see their team, department heads see their whole department. Removing
        someone ends the link from today; past records keep their team.
      </Typography>
      {data.departments.length === 0 && <Typography>No sales departments yet.</Typography>}
      {data.departments.map(d => {
        const teams = data.teams.filter(t => t.departmentId === d.id);
        return (
          <Paper key={d.id} data-help-section="sales.teams.department" sx={{ p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>{d.name}</Typography>
              <Button data-help="sales.teams.newTeam" size="small" startIcon={<AddIcon />} onClick={() => setNameTarget({ kind: 'newTeam', dept: d })}>New team</Button>
              <IconButton data-help="sales.teams.rename.department" size="small" aria-label="Rename department" onClick={() => setNameTarget({ kind: 'renameDepartment', dept: d })}><EditIcon fontSize="small" /></IconButton>
              <IconButton
                data-help="sales.teams.deleteDepartment" size="small" aria-label="Delete department" disabled={busy}
                onClick={() => safe(() => SalesTeamService.deleteDepartment(institutionId, d.id), 'Failed to delete department')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="subtitle2">Department heads</Typography>
            <LinkChips links={d.heads} busy={busy} onRemove={u => safe(() => SalesTeamService.removeHead(institutionId, d.id, u), 'Failed to remove head')} />
            <div data-help="sales.teams.addHead"><AddPerson label="Add head" busy={busy} onAdd={u => safe(() => SalesTeamService.addHead(institutionId, d.id, u), 'Failed to add head')} /></div>

            {teams.length === 0 && <Typography variant="body2" color="text.secondary">No teams in this department.</Typography>}
            {teams.map(t => (
              <Paper key={t.id} data-help-section="sales.teams.team" variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>{t.name}</Typography>
                  <IconButton data-help="sales.teams.rename.team" size="small" aria-label="Rename team" onClick={() => setNameTarget({ kind: 'renameTeam', team: t })}><EditIcon fontSize="small" /></IconButton>
                  <IconButton
                    data-help="sales.teams.deleteTeam" size="small" aria-label="Delete team" disabled={busy}
                    onClick={() => safe(() => SalesTeamService.deleteTeam(institutionId, t.id), 'Failed to delete team')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="subtitle2">Managers</Typography>
                <LinkChips links={t.managers} busy={busy} onRemove={u => safe(() => SalesTeamService.removeManager(institutionId, t.id, u), 'Failed to remove manager')} />
                <div data-help="sales.teams.addManager"><AddPerson label="Add manager" busy={busy} onAdd={u => safe(() => SalesTeamService.addManager(institutionId, t.id, u), 'Failed to add manager')} /></div>
                <Typography variant="subtitle2">Members</Typography>
                <LinkChips links={t.members} busy={busy} onRemove={u => safe(() => SalesTeamService.removeMember(institutionId, t.id, u), 'Failed to remove member')} />
                <div data-help="sales.teams.addMember"><AddPerson label="Add member" busy={busy} onAdd={u => safe(() => SalesTeamService.addMember(institutionId, t.id, u), 'Failed to add member')} /></div>
              </Paper>
            ))}
          </Paper>
        );
      })}
      {nameTarget && (
        <NameDialog title={nameDialogTitle} initial={nameDialogInitial} onClose={() => setNameTarget(null)} onSave={saveName} />
      )}
    </Box>
  );
};

export default SalesTeams;
